import { useEffect, useMemo, useState } from 'react';
import { Modal, Image, Form, Button, Input, Divider, Checkbox } from 'antd';

import { DOCUMITRA_ORDER_APPLICATION } from '../constants/EntityName';
import callApi from '../Api/config';
import { failedMsg } from '../constants/messages';
import { toastSuccess, toastError } from '../service/Toast';
import { fallback } from '../constants/FallbackImage';
import { BASE_IMAGE_URL } from '../env';
import { InvertFillButton } from './InvertFillButton';

const { TextArea } = Input;

const normalizePresets = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(item => (typeof item === 'string' ? item.trim() : '')).filter(Boolean);
};

const resolveRemarkPresetsFromOrder = (
  formDetails: any,
  documentName?: string,
  activePhoto?: any
): string[] => {
  const targetName = (
    documentName ||
    activePhoto?.name ||
    activePhoto?.documentName ||
    activePhoto?.docName ||
    ''
  )
    .toString()
    .trim()
    .toLowerCase();

  const fromPlan = normalizePresets(
    formDetails?.pricingPlan?.documents?.find(
      (d: any) => d?.name?.toString().trim().toLowerCase() === targetName
    )?.remarkPresets
  );
  if (fromPlan.length) {
    return fromPlan;
  }

  const members = formDetails?.members || [];
  for (const member of members) {
    const match = member?.documents?.find(
      (d: any) => d?.name?.toString().trim().toLowerCase() === targetName
    );
    const presets = normalizePresets(match?.remarkPresets);
    if (presets.length) {
      return presets;
    }
  }

  return [];
};

const PhotoRemarksModal = (props: any) => {
  const [form] = Form.useForm();
  const {
    activePhoto,
    openAddRemark,
    setOpenAddRemarks,
    formDetails,
    onRemarkUpdate,
    documentName,
    remarkPresets: remarkPresetsProp,
  } = props;
  const [remarks, setRemarks] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedPresets, setSelectedPresets] = useState<string[]>([]);

  const remarkPresets = useMemo(() => {
    const fromProp = normalizePresets(remarkPresetsProp);
    if (fromProp.length) {
      return fromProp;
    }
    return resolveRemarkPresetsFromOrder(formDetails, documentName, activePhoto);
  }, [remarkPresetsProp, formDetails, documentName, activePhoto]);

  const syncRemarksFromSelectedPresets = (nextSelected: string[], extraText?: string) => {
    const base = nextSelected.map(preset => `• ${preset}`).join('\n');
    const tail = extraText ? extraText.trim() : '';
    const combined = [base, tail].filter(Boolean).join('\n').trim();
    setRemarks(combined);
    form.setFieldValue(`${activePhoto?.id}-remarks`, combined);
  };

  const togglePreset = (preset: string) => {
    setSelectedPresets(prev => {
      const next = prev.includes(preset)
        ? prev.filter(selected => selected !== preset)
        : [...prev, preset];
      syncRemarksFromSelectedPresets(next);
      return next;
    });
  };

  const clearSelectedPresets = () => {
    setSelectedPresets([]);
    syncRemarksFromSelectedPresets([]);
  };

  const selectAllPresets = () => {
    const all = [...remarkPresets];
    setSelectedPresets(all);
    syncRemarksFromSelectedPresets(all);
  };

  const handleRemarks = (e: any) => {
    setRemarks(e.target.value);
    form.setFieldValue(`${activePhoto?.id}-remarks`, e.target.value);
  };

  const createEntity = async (payload: any, entityName: string) => {
    try {
      setSubmitting(true);
      const res: any = await callApi(`${entityName}/${formDetails?.id}`, 'PATCH', payload);
      if (res?.status) {
        // Update the activePhoto with the new remark for immediate tooltip update
        const updatedPhoto = { ...activePhoto, remark: payload.photo.remarks };

        setRemarks('');
        form.setFieldValue(`${activePhoto?.id}-remarks`, '');
        setSelectedPresets([]);
        setOpenAddRemarks(!openAddRemark);

        // Call the onRemarkUpdate callback to update parent component state
        if (onRemarkUpdate) {
          onRemarkUpdate(updatedPhoto);
        }

        toastSuccess(`${activePhoto?.remark ? 'Updated' : 'Added'} remarks successfully!`);
      } else {
        toastError(
          `Oops, an error occured! ${activePhoto?.remark ? 'Update' : 'Creation'} failed. ${res?.data?.message || failedMsg}`
        );
      }
    } catch (err) {
      toastError(
        `Oops, an error occured! ${activePhoto?.remark ? 'Update' : 'Creation'} failed. Please contact development support team`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageRemarks = (value: any) => {
    const payload = {
      photo: {
        photoId: activePhoto?.id,
        remarks: value?.[`${activePhoto?.id}-remarks`],
      },
    };
    createEntity(payload, props.entityName || DOCUMITRA_ORDER_APPLICATION);
    if (props.onSuccess) {
      props.onSuccess(activePhoto.id, value?.[`${activePhoto?.id}-remarks`]);
    }
  };

  useEffect(() => {
    if (activePhoto) {
      const existing = activePhoto?.remark || '';
      setRemarks(existing);
      form.setFieldValue(`${activePhoto?.id}-remarks`, existing);
      const normalized = (existing || '').toString();
      const matched = remarkPresets
        .filter(preset => normalized.includes(preset))
        .sort((a, b) => normalized.indexOf(a) - normalized.indexOf(b));
      setSelectedPresets(matched);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePhoto, openAddRemark, remarkPresets]);

  const resolvedDocName =
    activePhoto?.name ||
    documentName ||
    activePhoto?.documentName ||
    activePhoto?.docName ||
    activePhoto?.documentType ||
    activePhoto?.documentCategory ||
    'Document';

  return (
    <Modal
      key={activePhoto?.id}
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '4px',
                height: '24px',
                backgroundColor: '#004195',
                borderRadius: '2px',
              }}
            />
            <span
              style={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#0f172a',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {activePhoto?.remark ? 'Edit Remark' : 'Add Remark'}
            </span>
          </div>
          {activePhoto?.remark && (
            <span
              style={{
                fontSize: '13px',
                color: 'red',
                fontWeight: 600,
                gap: '50px',
                padding: '4px 12px',
                backgroundColor: '#ffd8d7',
                borderRadius: '999px',
                border: '1px solid red',
              }}
            >
              Has existing remarks
            </span>
          )}
        </div>
      }
      visible={openAddRemark}
      onOk={() => setOpenAddRemarks(!openAddRemark)}
      onCancel={() => setOpenAddRemarks(!openAddRemark)}
      width="70%"
      style={{ minWidth: '560px' }}
      footer={null}
      closeIcon={
        <div
          style={{
            fontSize: '24px',
            color: '#64748b',
            cursor: 'pointer',
            backgroundColor: '#e6e6e6',
            position: 'absolute',
            padding: '5px',
            height: '30px',
            width: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            top: '10px',

            borderRadius: '50%',
            right: '15px',
            lineHeight: 1,
            fontWeight: 300,
          }}
        >
          ×
        </div>
      }
    >
      <div>
        <div
          style={{
            fontSize: '22px',
            fontWeight: 700,
            color: '#004195',
            lineHeight: 1.3,
            marginBottom: '18px',
            paddingBottom: '14px',
            borderBottom: '1px solid #e2e8f0',
            wordBreak: 'break-word',
          }}
        >
          {resolvedDocName}
        </div>

        {activePhoto?.remark && (
          <div
            style={{
              marginBottom: '20px',
              padding: '16px',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
            }}
          >
            <div
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '8px',
              }}
            >
              Current Remark
            </div>
            <div
              style={{
                fontSize: '14px',
                color: '#334155',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
              }}
            >
              {activePhoto.remark}
            </div>
          </div>
        )}

        {remarkPresets.length > 0 ? (
          <>
            <div style={{ marginBottom: '12px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    flexWrap: 'wrap',
                  }}
                >
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#004195',
                      letterSpacing: '0.02em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Quick Remark Options
                  </span>
                  <span
                    style={{
                      fontSize: '12px',
                      color: '#64748b',
                      fontWeight: 600,
                      padding: '2px 10px',
                      borderRadius: '999px',
                      backgroundColor: '#eef2ff',
                      border: '1px solid #dbe4f2',
                    }}
                  >
                    {selectedPresets.length} selected
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <button
                    type="button"
                    onClick={selectAllPresets}
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '4px 10px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      backgroundColor: '#ffffff',
                      color: '#334155',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                      e.currentTarget.style.borderColor = '#94a3b8';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={clearSelectedPresets}
                    disabled={selectedPresets.length === 0}
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '4px 10px',
                      borderRadius: '6px',
                      border: '1px solid #fee2e2',
                      backgroundColor: selectedPresets.length === 0 ? '#f8fafc' : '#fff5f5',
                      color: selectedPresets.length === 0 ? '#94a3b8' : '#b91c1c',
                      cursor: selectedPresets.length === 0 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s ease',
                      opacity: selectedPresets.length === 0 ? 0.6 : 1,
                    }}
                    onMouseEnter={e => {
                      if (selectedPresets.length !== 0) {
                        e.currentTarget.style.backgroundColor = '#ffe0e0';
                        e.currentTarget.style.borderColor = '#fecaca';
                      }
                    }}
                    onMouseLeave={e => {
                      if (selectedPresets.length !== 0) {
                        e.currentTarget.style.backgroundColor = '#fff5f5';
                        e.currentTarget.style.borderColor = '#fee2e2';
                      }
                    }}
                  >
                    Clear All
                  </button>
                </div>
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#64748b',
                  marginBottom: '10px',
                  lineHeight: 1.5,
                }}
              >
                Tick any combination of remarks. All checked remarks are automatically joined into
                the remark text below.
              </div>
            </div>
            <div
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                backgroundColor: '#f8fafc',
                padding: '12px 14px',
                marginBottom: '20px',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
                  gap: '6px 18px',
                }}
              >
                {remarkPresets.map((preset, idx) => {
                  const isChecked = selectedPresets.includes(preset);
                  return (
                    <Checkbox
                      key={`${preset}-${idx}`}
                      checked={isChecked}
                      onChange={() => togglePreset(preset)}
                      style={{
                        padding: '6px 4px',
                        marginInlineEnd: 0,
                        fontSize: '13.5px',
                        lineHeight: 1.45,
                        color: isChecked ? '#004195' : '#334155',
                        fontWeight: isChecked ? 600 : 500,
                        borderRadius: '6px',
                        backgroundColor: isChecked ? '#e6eefb' : 'transparent',
                        transition: 'all 0.12s ease',
                      }}
                    >
                      {preset}
                    </Checkbox>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div
            style={{
              fontSize: '13px',
              color: '#64748b',
              marginBottom: '16px',
              padding: '10px 12px',
              borderRadius: '8px',
              backgroundColor: '#f8fafc',
              border: '1px dashed #cbd5e1',
            }}
          >
            No remark options configured for this document. Add them under Packages → Documents →
            Remarks, or type a custom remark below.
          </div>
        )}

        <div style={{ marginBottom: '12px' }}>
          <span
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#334155',
            }}
          >
            {activePhoto?.remark ? 'New Remark' : 'Remark'}
          </span>
        </div>
        <Form key={activePhoto?.id} form={form} onFinish={handleImageRemarks}>
          <Form.Item
            name={`${activePhoto?.id}-remarks`}
            style={{ marginBottom: '24px', width: '100%' }}
            rules={[
              {
                required: false,
                message: 'Please enter remark',
              },
            ]}
          >
            <TextArea
              style={{
                width: '100%',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                padding: '12px',
                minHeight: '110px',
                resize: 'vertical',
              }}
              id="remarks"
              rows={5}
              placeholder={activePhoto?.remark ? 'Enter updated remark...' : 'Enter your remark...'}
              value={remarks}
              onChange={handleRemarks}
            />
          </Form.Item>
          <Form.Item style={{ width: '100%', marginBottom: '0' }}>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                paddingTop: '12px',
                borderTop: '1px solid #f1f5f9',
              }}
            >
              <InvertFillButton
                variant="white"
                fontSize="base"
                fontWeight="medium"
                onClick={() => setOpenAddRemarks(!openAddRemark)}
              >
                Cancel
              </InvertFillButton>
              <InvertFillButton
                variant="blue"
                fontSize="base"
                fontWeight="medium"
                onClick={() => form.submit()}
                disabled={submitting}
              >
                {submitting
                  ? 'Processing...'
                  : activePhoto?.remark
                    ? 'Update Remark'
                    : 'Add Remark'}
              </InvertFillButton>
            </div>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default PhotoRemarksModal;
